import {groupBy} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {isEqual} from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {CommonAnalyticsActions} from 'constants/AnalyticsConstants';
import {
  areColumnDataTypesCompatible,
  ColumnDataTypeSpec,
  guessColumnDataType,
} from 'notebook/structured-tables/columns/ColumnDataType';
import isMixtureTableMappingEnabled from 'notebook/structured-tables/structured-table-mappings/isMixtureTableMappingEnabled';
import makeTableColumnProvenanceUuid from 'notebook/structured-tables/structured-table-mappings/makeTableColumnProvenanceUuid';
import {
  CompatibleColumnInfoMap,
  CompatibleStructuredTableInfo,
  MIXTURE_TABLE_MAPPING_KINDS,
  SOURCE_TABLE_TYPES,
  CompatibleStructuredTableColumnInfo,
  StructuredTableMapping,
  StructuredTableColumnMapping,
  TableColumnProvenanceUuid,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {trackStructuredTableMappingConfigModalEvent} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsAnalytics';
import {
  StructuredTableMappingsState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import TableColumnSelectTreeInput from 'notebook/structured-tables/structured-table-mappings/TableColumnSelectTreeInput';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTableColumn,
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import Modal from 'ui-library/modals/Modal';
import removeNulls from 'util/removeNulls';
import {NumericInputChangeEvent} from 'util/Types';

export interface StructuredTableMappingConfigModalProps {
  structuredTableMappingsState: StructuredTableMappingsState;
  structuredTables: Array<UnknownStructuredTable>;
  destinationTable: UnknownStructuredTable;
  destinationColumn: StructuredTableColumn;
  destinationColumnDataType: ColumnDataTypeSpec;
  onRequestClose: () => void;
  onRequestSubmit: (structuredTableMappingsState: StructuredTableMappingsState) => void;
  analyticsParams: {
    sessionId: string;
    fileId: string;
  };
}

export default function StructuredTableMappingConfigModal({
  structuredTables,
  destinationTable,
  destinationColumn,
  destinationColumnDataType,
  structuredTableMappingsState,
  onRequestClose,
  onRequestSubmit,
  // Analytics
  analyticsParams,
}: StructuredTableMappingConfigModalProps): JSX.Element {
  const [selectedTableColumnIds, setSelectedTableColumnIds] = useState<Array<TableColumnProvenanceUuid>>(() =>
    getCurrentSelectedSourceTableColumnIds(
      structuredTables,
      structuredTableMappingsState,
      destinationTable,
      destinationColumn
    )
  );
  const [replicationFactor, setReplicationFactor] = useState<number | null>(
    () =>
      StructuredTableMappingsStateManager.getReplicationFactorForDestinationTable(
        structuredTableMappingsState,
        destinationTable.provenanceUuid
      ) ?? 1
  );
  const [isReplicationFactorValid, setIsReplicationFactorValid] = useState<boolean>(true);
  // it's a bit strange to set the ref from the state (rather than the opposite), but this is so that we don't
  // have to recalculate the initialSelectedTableColumnIds on every render because only useState allows for
  // functional initialization
  const initialSelectedTableColumnIds = useRef(selectedTableColumnIds);
  const compatibleSourceTableInfos = useMemo(
    () =>
      getCompatibleSourceTableInfos(
        // remove the current table from the list of other tables
        structuredTables.filter((table) => table.provenanceUuid !== destinationTable.provenanceUuid),
        destinationColumnDataType
      ),
    [structuredTables, destinationColumnDataType, destinationTable.provenanceUuid]
  );
  const {columnInfoMap: sourceColumnInfoMap} = useMemo(
    () => makeTableAndColumnUuidMaps(compatibleSourceTableInfos),
    [compatibleSourceTableInfos]
  );

  const onSubmit = useCallback(() => {
    const sourceColumnInfos = selectedTableColumnIds.map(
      (tableColumnId) => sourceColumnInfoMap[tableColumnId]
    );
    const sourceColumnInfosBySourceTableUuid = groupBy(
      sourceColumnInfos,
      (columnInfo) => columnInfo.structuredTable.provenanceUuid
    );
    const destinationTableMappings: Array<StructuredTableMapping> = Object.entries(
      sourceColumnInfosBySourceTableUuid
    ).map(
      ([sourceTableProvenanceUuid, columnInfos]): StructuredTableMapping => ({
        sourceTableProvenanceUuid,
        destinationTableProvenanceUuid: destinationTable.provenanceUuid,
        // replicationFactor is not null because we only call onSubmit when isReplicationFactorValid is true
        replicationFactor: replicationFactor!,
        structuredTableColumnMappings: columnInfos.map(
          (columnInfo): StructuredTableColumnMapping => ({
            sourceTableColumnProvenanceUuid: columnInfo.structuredTableColumn.provenanceUuid,
            destinationTableColumnProvenanceUuid: destinationColumn.provenanceUuid,
          })
        ),
      })
    );
    const updatedState: StructuredTableMappingsState =
      StructuredTableMappingsStateManager.replaceDestinationColumnMappings(
        structuredTableMappingsState,
        destinationTable.provenanceUuid,
        destinationColumn.provenanceUuid,
        destinationTableMappings
      );
    if (!isEqual(structuredTableMappingsState, updatedState)) {
      onRequestSubmit(updatedState);
      trackStructuredTableMappingConfigModalEvent(
        analyticsParams.sessionId,
        CommonAnalyticsActions.SUBMITTED_MODAL,
        analyticsParams.fileId,
        destinationTable
      );
    } else {
      // if nothing changed, just close the modal
      onRequestClose();
      trackStructuredTableMappingConfigModalEvent(
        analyticsParams.sessionId,
        CommonAnalyticsActions.CLOSED_MODAL,
        analyticsParams.fileId,
        destinationTable,
        {
          hadChanges: false,
        }
      );
    }
  }, [
    selectedTableColumnIds,
    structuredTableMappingsState,
    destinationTable,
    destinationColumn.provenanceUuid,
    sourceColumnInfoMap,
    replicationFactor,
    onRequestSubmit,
    analyticsParams.sessionId,
    analyticsParams.fileId,
    onRequestClose,
  ]);

  const onClose = useCallback(() => {
    onRequestClose();
    trackStructuredTableMappingConfigModalEvent(
      analyticsParams.sessionId,
      CommonAnalyticsActions.CLOSED_MODAL,
      analyticsParams.fileId,
      destinationTable,
      {
        hadChanges: !isEqual(initialSelectedTableColumnIds.current, selectedTableColumnIds),
      }
    );
  }, [
    analyticsParams.fileId,
    analyticsParams.sessionId,
    destinationTable,
    onRequestClose,
    selectedTableColumnIds,
  ]);

  const hasPotentialMappings: boolean = Boolean(compatibleSourceTableInfos.length);
  const hasSelectedColumns: boolean = selectedTableColumnIds.length > 0;

  function getSaveDisabledTooltip(): string | null {
    if (!hasPotentialMappings) {
      return 'No compatible columns available';
    }
    if (!isReplicationFactorValid) {
      return 'Enter a valid number of replicates';
    }
    if (!hasSelectedColumns) {
      return 'Select at least one column to save';
    }
    return null;
  }

  return (
    <Modal
      title={`Mappings to column "${destinationColumn.name}"`}
      buttons={[
        {
          text: 'Save',
          submit: true,
          disabled: getSaveDisabledTooltip() != null,
          tooltipTitle: getSaveDisabledTooltip(),
        },
      ]}
      onRequestClose={onClose}
      onRequestSubmit={onSubmit}
    >
      {hasPotentialMappings ? (
        <div>
          <TableColumnSelectTreeInput
            selectedTableColumnIds={selectedTableColumnIds}
            setSelectedTableColumnIds={setSelectedTableColumnIds}
            compatibleTableInfos={compatibleSourceTableInfos}
            columnInfoMap={sourceColumnInfoMap}
          />
          <FieldLabel
            label="Number of replicates"
            isRequired={true}
            htmlFor="table-mapping-replication-factor"
          />
          <NumericInput
            id="table-mapping-replication-factor"
            min={1}
            className={classNames('u-small-spacing-left', css(styles.replicationFactorInput))}
            required={true}
            onChange={(e: NumericInputChangeEvent) => {
              setReplicationFactor(e.target.numericValue);
              setIsReplicationFactorValid(e.target.isValid);
            }}
            value={replicationFactor}
            validateInteger={true}
          />
        </div>
      ) : (
        <div>No other columns are compatible with this destination column.</div>
      )}
    </Modal>
  );
}

/** Whether a structured table of the given kind is allowed to be a Table Mapping source, accounting for the
 * config flags that gate certain kinds (LOOKUP, Mixture Prep tables). */
export function isAllowedSourceTableKind(kind: StructuredTableKind): boolean {
  if (!SOURCE_TABLE_TYPES.has(kind)) {
    return false;
  }
  if (kind === StructuredTableKind.LOOKUP && !BenchlingConfig.ELN_ENABLE_LOOKUP_TABLE_MAPPING) {
    return false;
  }
  if (MIXTURE_TABLE_MAPPING_KINDS.has(kind) && !isMixtureTableMappingEnabled()) {
    return false;
  }
  return true;
}

/** Given a destination column and potential source tables, determine which tables and columns can be mapped
 * to the destination */
export function getCompatibleSourceTableInfos(
  structuredTables: Array<UnknownStructuredTable>,
  destinationColumnDataType: ColumnDataTypeSpec
): Array<CompatibleStructuredTableInfo> {
  const compatibleTableInfos: Array<CompatibleStructuredTableInfo> = [];
  const possibleStructuredTables = structuredTables.filter((structuredTable) =>
    isAllowedSourceTableKind(structuredTable.kind)
  );
  for (const table of possibleStructuredTables) {
    const compatibleColumns: Array<CompatibleStructuredTableColumnInfo> = [];
    const columnDefinitions = getStructuredTableAdapterFromTable(table).getColumnDefinitions(table);
    for (let columnIndex = 0; columnIndex < table.columns.length; columnIndex++) {
      const columnDefinition = columnDefinitions[columnIndex];
      const sourceColumnDataType = guessColumnDataType(columnDefinition, table);
      if (
        sourceColumnDataType &&
        areColumnDataTypesCompatible(sourceColumnDataType, destinationColumnDataType)
      ) {
        compatibleColumns.push({
          columnIndex,
          structuredTableColumn: table.columns[columnIndex],
          columnDefinition,
          columnDataType: sourceColumnDataType,
          structuredTable: table,
        });
      }
    }
    if (compatibleColumns.length) {
      compatibleTableInfos.push({
        structuredTable: table,
        compatibleStructuredTableColumns: compatibleColumns,
      });
    }
  }
  return compatibleTableInfos;
}

export function makeTableAndColumnUuidMaps(compatibleTableInfos: Array<CompatibleStructuredTableInfo>): {
  tableInfoMap: Record<TableColumnProvenanceUuid, CompatibleStructuredTableInfo>;
  columnInfoMap: CompatibleColumnInfoMap;
} {
  const tableMap: Record<string, CompatibleStructuredTableInfo> = {};
  const columnMap: CompatibleColumnInfoMap = {};
  for (const compatibleTable of compatibleTableInfos) {
    const structuredTable = compatibleTable.structuredTable;
    tableMap[structuredTable.provenanceUuid] = compatibleTable;
    for (const compatibleColumn of compatibleTable.compatibleStructuredTableColumns) {
      const tableColumnId = makeTableColumnProvenanceUuid(
        structuredTable.provenanceUuid,
        compatibleColumn.structuredTableColumn.provenanceUuid
      );
      columnMap[tableColumnId] = compatibleColumn;
    }
  }
  return {columnInfoMap: columnMap, tableInfoMap: tableMap};
}

/** Get the selected mappings from the current state. Also checks existing Structured Tables to ensure that
 * mappings are actually valid. */
function getCurrentSelectedSourceTableColumnIds(
  structuredTables: Array<UnknownStructuredTable>,
  structuredTableMappingsState: StructuredTableMappingsState,
  destinationTable: UnknownStructuredTable,
  destinationColumn: StructuredTableColumn
): Array<TableColumnProvenanceUuid> {
  return removeNulls(
    StructuredTableMappingsStateManager.getMappingsForDestinationColumn(
      structuredTableMappingsState,
      destinationTable.provenanceUuid,
      destinationColumn.provenanceUuid
    ).flatMap((tableMapping) => {
      const structuredTable = structuredTables.find(
        (table) => table.provenanceUuid === tableMapping.sourceTableProvenanceUuid
      );
      if (!structuredTable) {
        return null;
      }
      return tableMapping.structuredTableColumnMappings.map((columnMapping) => {
        if (
          !structuredTable.columns.some(
            (column) => column.provenanceUuid === columnMapping.sourceTableColumnProvenanceUuid
          )
        ) {
          return null;
        }
        return makeTableColumnProvenanceUuid(
          tableMapping.sourceTableProvenanceUuid,
          columnMapping.sourceTableColumnProvenanceUuid
        );
      });
    })
  );
}

const styles = StyleSheet.create({
  replicationFactorInput: {
    width: 50,
  },
});
